import { Avatar, AvatarGroup, Text, Box, Flex } from '@chakra-ui/react';
import { useSendMessage, useSessionInfo, useVendor } from '@outdoorly/react-chat-sdk';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

import { EXPERT_IMAGES } from '../../constants';
import { ChatMessage, MessageOptionActionType } from '../Message/ChatMessage';
import { TypingIndicator } from '../MessageList/TypingIndicator';
import { motion } from 'framer-motion';
import { MessageOptions } from '../Message/MessageOptions';
import { Store } from '../../store';
import { clearIntroFields, getIntroField, initializeIntroStorage, setIntroField } from '../../utils';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { IntroMessageCollection } from './IntroMessageCollection';
import { IntroChatButton } from './IntroChatButton';
import { IntroBullet } from './IntroBullet';
import { EmojiAndLinkifiedText } from '../Message/EmojiAndLinkifiedText';
import posthog from 'posthog-js';
import { IntroPIICollection } from './IntroPIICollection';
import { substituteLabelVariables } from '../../utils/labels';
import { IntroductionMessage } from './IntroductionMessage';

type IntroSequenceProps = {
  showWaitingForExpert: (arg0: boolean) => void;
  showWaitForLastMessage: (arg0: boolean) => void;
  scrollToBottom: () => void;
};
const FAKE_MESSAGE_TIMEOUT = 800;
export type MessageActionOptions = {
  text: string;
  actionType: MessageOptionActionType;
  action?: () => void;
  cta?: boolean;
};

type IntroMessage = {
  body: JSX.Element;
  options: Array<MessageActionOptions> | null;
  isLastMessageInGroup: boolean;
  dateUpdated: string;
  author: string;
  id: string;
  disableAriaLive?: boolean;
};

export const IntroSequence: React.FC<IntroSequenceProps> = (props) => {
  const { showWaitingForExpert, showWaitForLastMessage, scrollToBottom } = props;
  const labels = Store.useState((s) => s.labels);
  const [messageList, setMessageList] = useState<Array<IntroMessage>>([]);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [activatedTOS, setActivatedTOS] = useState(false);
  const [expertHelpActivated, setExpertHelpActivated] = useState(false);
  const messageSender = useSendMessage();
  const { vendor } = useVendor();
  const { sessionInfo, initialized } = useSessionInfo();

  const INTRO_MESSAGE_LIST = useMemo(
    () => [
      {
        body: <IntroductionMessage setExpertHelpActivated={setExpertHelpActivated} showActivateChatButton />,
        isLastMessageInGroup: false,
        dateUpdated: getIntroField('startTime') || dayjs().toString(),
        options: null,
        author: 'system',
        id: '5821c9f5-5f0d-4264-83dc-cf77976203a8',
        disableAriaLive: true
      },
      {
        isLastMessageInGroup: true,
        dateUpdated: getIntroField('messageTime-75313b30-a018-4699-a361-daabacef96ba') || dayjs().toString(),
        author: 'user',
        options: null,
        id: '0deceb65-e82e-4301-9187-dcf3dbd03751',
        body: (
          <Text variant="paragraph-medium" color="scheme.contentInversePrimary">
            {labels.chatWithExpert}
          </Text>
        )
      },
      {
        body: (
          <IntroMessageCollection
            submitMessage={(message) => {
              setIntroField('introMessageToExpert', message);
              setIntroField('introMessageTime', dayjs().toString());
              setSentMessage(message);
            }}
          />
        ),
        options: null,
        isLastMessageInGroup: true,
        dateUpdated: getIntroField('messageTime-75313b30-a018-4699-a361-daabacef96ba') || dayjs().toString(),
        author: 'system',
        id: '75313b30-a018-4699-a361-daabacef96ba',
        disableAriaLive: true
      },
      {
        body: getIntroField('introMessageToExpert') ? (
          <Text variant="paragraph-medium" color="scheme.contentInversePrimary">
            {getIntroField('introMessageToExpert')}
          </Text>
        ) : (
          <></>
        ),
        options: null,
        isLastMessageInGroup: true,
        dateUpdated: getIntroField('introMessageTime') || dayjs().toString(),
        author: 'user',
        id: '26206070-91c4-4e12-8bda-51c3ee32f399'
      },
      {
        isLastMessageInGroup: false,
        dateUpdated: getIntroField('messageTime-e0b1a6d9-f618-4c59-b99d-0a3596f84ee2') || dayjs().toString(),
        author: 'system',
        options: null,
        id: 'e0b1a6d9-f618-4c59-b99d-0a3596f84ee2',
        body: <Text variant="paragraph-medium">{labels.introToPII}</Text>
      },
      {
        body: (
          <IntroPIICollection
            submitName={(name: string) => {
              setIntroField('remarkIntroMessageName', name);
              messageSender({ type: 'identify', firstName: name });
              setActivatedTOS(true);
            }}
            skipName={() => {
              setIntroField('remarkIntroMessageNameSkipped', 'skipped');
              setActivatedTOS(true);
            }}
            submitEmail={(email) => {
              setIntroField('introMessageEmail', email);
              setIntroField('introMessage-fdcaa3b2-8b15-4a19-8e7c-890e87d130b9', email);
              setEmail(email);
              messageSender({ type: 'identify', email });
            }}
            skipEmail={() => {
              setIntroField('introMessage-fdcaa3b2-8b15-4a19-8e7c-890e87d130b9', 'skip');
              setEmail('skip');
              Store.update((s) => {
                s.skippedEmail = true;
              });
            }}
          />
        ),
        options: null,
        isLastMessageInGroup: true,
        dateUpdated: getIntroField('messageTime-fdcaa3b2-8b15-4a19-8e7c-890e87d130b9') || dayjs().toString(),
        author: 'system',
        id: 'fdcaa3b2-8b15-4a19-8e7c-890e87d130b9',
        disableAriaLive: true
      },
      {
        isLastMessageInGroup: false,
        dateUpdated: getIntroField('messageTime-cd2343e3-3d74-4151-9038-f6c414e52e4f') || dayjs().toString(),
        author: 'system',
        options: null,
        id: 'cd2343e3-3d74-4151-9038-f6c414e52e4f',
        body: (
          <Text variant="paragraph-medium" color="scheme.contentTertiary">
            By continuing, you agree to our{' '}
            <a href="https://www.withremark.com/terms-of-service" target="_blank">
              Terms of Use
            </a>{' '}
            and{' '}
            <a href="https://www.withremark.com/privacy" target="_blank">
              Privacy Policy
            </a>
          </Text>
        )
      },
      {
        body: <Text variant="paragraph-medium">{labels.matchingYouWithExpertWithoutName}</Text>,
        options: null,
        isLastMessageInGroup: true,
        dateUpdated: dayjs().toString(),
        author: 'system',
        id: 'b66f7660-f33e-477d-9ff5-e91f8b2b37a9'
      }
    ],
    [vendor]
  );

  const [showTypingIndicator, setShowTypingIndicator] = useState(false);

  const hasSentIntroMessage = useMemo(() => getIntroField('introMessageToExpert'), []);

  const vendorLogo = useMemo(() => {
    if (vendor?.logo) {
      return tryToBuildImgixUrl(vendor.logo, { w: 50, h: 50, auto: 'format,compress' });
    }
    return undefined;
  }, [vendor]);

  const isIdentified = useMemo(() => sessionInfo?.isIdentified, []);

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];

    initializeIntroStorage();

    const wantsExpertHelp = getIntroField(`introMessage-fa3fc71d-2ba4-46f6-a354-d42a4a04443e`);

    const hasCollectedOrSkippedFirstName =
      getIntroField('remarkIntroMessageName') || getIntroField('remarkIntroMessageNameSkipped');

    setMessageList(
      hasSentIntroMessage
        ? (INTRO_MESSAGE_LIST.splice(0, hasCollectedOrSkippedFirstName ? 7 : 6) as Array<IntroMessage>)
        : wantsExpertHelp
        ? (INTRO_MESSAGE_LIST.splice(0, 3) as Array<IntroMessage>)
        : []
    );
    scrollToBottom();
    if (!wantsExpertHelp && !hasSentIntroMessage) {
      setShowTypingIndicator(true);
      const firstMessage = INTRO_MESSAGE_LIST.shift();

      timeouts.push(
        setTimeout(() => {
          setShowTypingIndicator(false);
          setMessageList([firstMessage] as Array<IntroMessage>);

          scrollToBottom();
        }, 2000)
      );
    }

    return () => {
      timeouts.map((t) => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    if (expertHelpActivated) {
      const updatedMessageList = [
        ...messageList,
        { ...INTRO_MESSAGE_LIST.shift(), dateUpdated: dayjs().toString() } as IntroMessage
      ];
      setMessageList(updatedMessageList);
      sendNewMessage({ updatedMessageList, skipMessages: 0 });
      setExpertHelpActivated(false);
    }
  }, [expertHelpActivated]);
  useEffect(() => {
    if (activatedTOS) {
      const updatedMessageList = [
        ...messageList,
        { ...INTRO_MESSAGE_LIST.shift(), dateUpdated: dayjs().toString() } as IntroMessage
      ];
      setMessageList(updatedMessageList);
      scrollToBottom();
    }
  }, [activatedTOS]);

  useEffect(() => {
    if (sentMessage) {
      const updatedMessageList = [
        ...messageList,
        {
          ...INTRO_MESSAGE_LIST.shift(),
          body: (
            <Text variant="paragraph-medium" color="scheme.contentInversePrimary">
              <EmojiAndLinkifiedText body={sentMessage} id={'sent-intro-customer-message'} />
            </Text>
          ),
          dateUpdated: dayjs().toString()
        } as IntroMessage
      ];
      setMessageList(updatedMessageList);
      sendNewMessage({
        updatedMessageList,
        skipMessages: isIdentified ? 3 : 0,
        sendExtraMessages: isIdentified ? undefined : 2
      });
      if (isIdentified) {
        findExpert();
      }
    }
  }, [sentMessage]);
  const findExpert = async () => {
    showWaitForLastMessage(true);
    await new Promise((r) => setTimeout(r, FAKE_MESSAGE_TIMEOUT));
    Store.update((s) => {
      s.muted = true;
    });
    messageSender(getIntroField('introMessageToExpert'));
    await new Promise((r) => setTimeout(r, FAKE_MESSAGE_TIMEOUT + 3000));
    showWaitingForExpert(true);
    showWaitForLastMessage(false);
    await new Promise((r) => setTimeout(r, FAKE_MESSAGE_TIMEOUT + 3000));
    Store.update((s) => {
      s.muted = Boolean(localStorage.getItem('mutedChat'));
    });
    showWaitingForExpert(false);
    clearIntroFields();
  };
  useEffect(() => {
    const outroSequence = () => {
      const updatedMessageList = [
        ...messageList,
        {
          ...INTRO_MESSAGE_LIST.shift(),
          body: (
            <Text variant="paragraph-medium">
              {getIntroField('remarkIntroMessageName')
                ? substituteLabelVariables(labels.matchingYouWithExpertWithName, {
                    firstName: getIntroField('remarkIntroMessageName')
                  })
                : labels.matchingYouWithExpertWithoutName}
            </Text>
          ),
          dateUpdated: dayjs().toString()
        } as IntroMessage
      ];
      setMessageList(updatedMessageList);
      findExpert();
    };
    if (email) {
      outroSequence();
    }
  }, [email]);
  let groupedMessage: Array<JSX.Element> = [];
  let messageOptions: JSX.Element | null = null;
  const sendNewMessage = (props: {
    updatedMessageList?: Array<IntroMessage>;
    skipMessages?: number;
    sendExtraMessages?: number;
  }) => {
    const { updatedMessageList, skipMessages, sendExtraMessages } = props;
    setShowTypingIndicator(true);
    if (skipMessages) {
      for (let i = 0; i < skipMessages; i++) {
        INTRO_MESSAGE_LIST.shift();
      }
    }
    setTimeout(() => {
      setShowTypingIndicator(false);
      let messages: IntroMessage[] = [];
      for (let j = 0; j < (sendExtraMessages || 1); j++) {
        const messageToAdd = INTRO_MESSAGE_LIST.shift() as IntroMessage;
        setIntroField(`messageTime-${messageToAdd.id}`, dayjs().toString());
        messages.push({ ...messageToAdd, dateUpdated: dayjs().toString() });
      }
      if (updatedMessageList) {
        setMessageList([...updatedMessageList, ...messages]);
        scrollToBottom();
      } else {
        setMessageList([...messageList, ...messages]);
        scrollToBottom();
      }
    }, FAKE_MESSAGE_TIMEOUT);
  };

  return (
    <>
      {messageList.map((message, index) => {
        const isSystem = message.author === 'system';
        const currentUserId = message.author;
        const nextUserId = messageList[index + 1]?.author;
        const isLastMessageInGroup = Boolean(
          messageList.length === index - 1 || currentUserId !== nextUserId || message?.options
        );

        const messageElement = (
          <ChatMessage
            isExpertOrSystem={isSystem}
            body={message.body}
            isLastMessageInGroup={isLastMessageInGroup}
            dateUpdated={message.dateUpdated}
            id={message.id}
            key={message.id}
            isLastMessage={index === messageList.length - 1}
            isIntroSequence={true}
            disableAriaLive={message.disableAriaLive}
          />
        );

        if (message.options)
          messageOptions = (
            <MessageOptions
              options={message.options}
              sendNewMessage={sendNewMessage}
              id={message.id}
              isLastMessage={index === messageList.length - 1}
              pastAnswer={getIntroField(`introMessage-${message.id}`)}
              // disableAnimation={hasSentIntroMessage ? index <= 5 : wantsExpertHelp ? index <= 3 : false}
              disableAnimation={false} // No options on messages currently
              findExpert={findExpert}
            />
          );

        if (nextUserId === message.author && !message?.options) {
          groupedMessage.push(messageElement);
        } else {
          const messageGroup = [...groupedMessage, messageElement];
          const options = messageOptions;
          groupedMessage = [];
          messageOptions = null;
          return (
            <Box key={message.id}>
              <Flex pos="relative" w="100%" key={message.id}>
                <motion.div
                  style={{ position: 'absolute' }}
                  initial={{ bottom: '0px', opacity: 0 }}
                  animate={{ bottom: '20px', opacity: 1 }}
                >
                  {isSystem && isLastMessageInGroup && (
                    <Avatar src={vendorLogo} w="24px" h="24px" ignoreFallback role="presentation" />
                  )}
                </motion.div>
                <Flex w="100%" flexDir="column">
                  {messageGroup}
                </Flex>
              </Flex>
              {options}
            </Box>
          );
        }
      })}
      {messageList.length === 1 && (
        <Text
          color="scheme.contentSecondary"
          textAlign="right"
          textDecoration="underline"
          variant="label-medium"
          cursor="pointer"
          my="8px"
          mr="12px"
          onClick={() => {
            posthog.capture('SUPPORT-LINK');
            window.open(vendor?.supportLink ?? '/');
          }}
        >
          {labels.customerSupport}
        </Text>
      )}
      <TypingIndicator
        botTyping={showTypingIndicator}
        avatar={vendorLogo}
        scrollToBottom={() => scrollToBottom()}
      />
    </>
  );
};
